<template>
  <div>
    <Loading :visible="!mapObject || !data" />
    <BreadcrumbMapObject v-if="mapObject"
                         :mapObject="mapObject"
                         :extraLabel="$t('button.edit')"
                         @clickBack="clickBack"
                         :show-parents="false"
    />
    <CollapsePanel
      :collapsed="true"
      :loading=loadingAttributes
      :showCount=false
      :title="$t('route_management.object_information')"
    >
      <div v-if="mapObject && data" class="object-information-block">
        <span>{{ $t('model.type') | capitalize }}</span>
        <p>
          <span class="thick">{{ typeLabel }}</span>
          <a class="pull-to-right" v-on:click="clickChangeType">
            <font-awesome-icon icon="pen" /> {{ $t('actionlog.change') | capitalize }} <br>
          </a>
        </p>
        <ChangeType v-if="this.changetype"
                    :is-active="this.changetype"
                    :object="this.data"
                    @close="clickChangeType" />
        <span> {{ $t('route_management.object_parents') | capitalize }}</span>
        <p>
          <span v-if="this.parent && this.parent.map_object_type_id" class="thick">
            {{ parentTypeLabel }} {{this.parent.name}}
          </span>
          <span v-else class="thick">
            {{ $tc('models.network',1) | capitalize }} {{this.network.name}}
          </span>
          <a v-if="this.parent && this.parent.map_object_type_id"
             class="pull-to-right"
             @click="ChangeParent"
          >
            <font-awesome-icon icon="pen"/> {{ $t('actionlog.change') | capitalize }} <br>
          </a>
        </p>
        <label for="input-field-name">* {{ $t('model.name') | capitalize }} </label>
        <div id="input-field-name" class="field">
          <input
            id="name"
            name="name"
            type="text"
            v-model="data.name"
            :class="{'error': errors.name !== null}"
            @change="resetError('name')"
            required
          />
          <span class="input-error">
            {{ errors.name }}
        </span>
        </div>
        <label v-if="mapObjectLock !== null" for="input-field-owner"> {{ $t('model.owner') | capitalize }} </label>
        <div v-if="mapObjectLock !== null" id="input-field-owner" class="field">
          <select v-model="data.created_by">
            <option :value="user.id" :key="user.id"
                    v-for="user in users">
              <span v-if="mapObjectLock.value === 'user'">
                  {{ user.name }}
              </span>
              <span v-else-if="mapObjectLock.value === 'team'">
                {{ user.name }}
                <span v-if="user.team && user.team.data && user.team.data.length > 0">
                  ({{ user.team.data.map(item => item.name).join(', ') }})
                </span>
              </span>
            </option>
          </select>
        </div>
        <span class="input-error">
          {{ errors.name }}
        </span>
        <label for="input-field-status">
          {{ $t('model.status') | capitalize }}
        </label>
        <div id="input-field-status" class="field">
          <MultiSelect
            id="map_object_status_id"
            :options="Object.values(mapObjectStatuses)"
            v-model="mapObjectStatus"
            track-by="id"
            label="name"
            :multiple="false"
            :close-on-select="true"
            :allow-empty="false"
            :preselect-first="true"
            placeholder=""
            selectLabel=""
            selectedLabel=""
            deselectLabel=""
            @input="updateStatus"
          />
        </div>
        <div id="attributes" v-if="data.attribute && !loadingAttributes">
          <div v-for="attribute in data.attribute.data" :key="attribute.id">
            <span v-if="attribute.required" class="title">
              <span class="required">*</span>
              {{ attribute.name }}
              <small v-if="typeLabelAttribute === attribute.id">
                ({{ $t('drawing.type_label_attribute') }})
              </small>
              :
            </span>
            <span v-else class="title">
              {{ attribute.name }}
              <small v-if="typeLabelAttribute === attribute.id">
                ({{ $t('drawing.type_label_attribute') }})
              </small>
              :
            </span>
            <div class="field content">
              <AttributeInput
                :disabled="false"
                :attribute="attribute"
                :error="errors[attribute.id]"
                :setDefault="false"
              />
            </div>
          </div>
        </div>
      </div>
    </CollapsePanel>
    <ButtonRow @back="clickBack" @save="saveObject" />
    <ChangeParent
      v-if="mapObject && parent"
      :visible="showChangeParent"
      :object="data"
      :parent="parent"
      @closeModal="ChangeParent"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import MultiSelect from 'vue-multiselect';

import CollapsePanel from '@/components/CollapsePanel.vue';
import MapObjectApi from '@/api/map_object';
import AttributeInput from '@/components/AttributeInput.vue';
import MapObjectTypeApi from '@/api/map_object_type';
import Loading from '@/components/Loading.vue';
import BreadcrumbMapObject from '@/components/Layout/BreadcrumbMapObject.vue';
import ButtonRow from '@/components/ButtonRow.vue';
import EventBus from '@/events/event-bus';
import MapMode from '@/enums/MapMode';
import ChangeParent from '@/modules/drawing/ChangeParent.vue';
import ChangeType from '@/modules/drawing/ChangeType.vue';
import * as dataCache from '@/store/cache/dataCache';

export default {
  name: 'drawing.edit_object',
  components: {
    ButtonRow,
    BreadcrumbMapObject,
    Loading,
    CollapsePanel,
    AttributeInput,
    ChangeParent,
    ChangeType,
    MultiSelect,
  },
  computed: {
    ...mapGetters({
      keyValues: 'settings/getKeyValues',
      network: 'settings/getNetworkModel',
      users: 'settings/getUsers',
      mapObject: 'selected/getMapObject',
      mapObjectTypes: 'settings/getMapObjectTypes',
    }),
    typeLabel() {
      if (!this.mapObjectTypes) {
        return '';
      }
      if (!this.mapObject) {
        return '';
      }
      if (!this.mapObjectTypes[this.mapObject.map_object_type_id]) {
        return '';
      }
      return this.mapObjectTypes[this.mapObject.map_object_type_id].name;
    },
    typeLabelAttribute() {
      if (!this.mapObjectTypes) {
        return null;
      }
      if (!this.mapObject) {
        return null;
      }
      if (!this.mapObjectTypes[this.mapObject.map_object_type_id]) {
        return null;
      }
      return this.mapObjectTypes[this.mapObject.map_object_type_id].label_attribute_id;
    },
    parentTypeLabel() {
      if (!this.mapObjectTypes) {
        return '';
      }
      if (!this.parent) {
        return '';
      }
      if (!this.mapObjectTypes[this.parent.map_object_type_id]) {
        return '';
      }
      return this.mapObjectTypes[this.parent.map_object_type_id].name;
    },
    mapObjectLock() {
      if (!this.network || !this.network.id) {
        return null;
      }
      const lockItems = Object.values(this.keyValues)
        .filter(item => item.key.includes(`lock_mapobject_${this.network.id}`));
      if (lockItems.length > 0) {
        return lockItems[0];
      }
      return null;
    },
  },
  data() {
    return {
      mapObjectStatuses: [],
      mapObjectStatus: null,
      data: null,
      parent: null,
      loading: true,
      loadingAttributes: true,
      changetype: false,
      errors: {
        name: null,
      },
      showChangeParent: false,
    };
  },
  methods: {
    ...mapActions({
      fetchMapObject: 'selected/fetchMapObject',
      updateMapObject: 'settings/updateMapObject',
      getMapObjectById: 'settings/getMapObjectById',
      resetSelected: 'map/resetSelected',
      addSelected: 'map/addSelected',
    }),
    saveObject() {
      if (!this.validateFields()) {
        return;
      }
      MapObjectApi.updateMapObjectById(this.mapObject.id, this.data)
        .then((response) => {
          this.$notify({
            type: 'success',
            title: this.$t('dialog.changes_saved'),
          });
          this.updateLocalObjects(response.data);
          this.fetchMapObject({ id: this.mapObject.id, force: true });
          EventBus.$emit('Map.changeMapMode', MapMode.objectSelect);
          this.$router.push({ name: 'drawing.show', params: { id: this.mapObject.id } });
        })
        .catch((error) => {
          this.$notify({
            type: 'error',
            title: this.$t('error.update_failed', { model: this.$tc('models.mapobject', 1) }),
            data: error.response,
          });
        });
    },
    updateLocalObjects(data) {
      console.log('updateLocalObjects', data, this.mapObject);
      const mapObject = data;
      let updatedLabel = false;
      if (!mapObject) return;
      if (this.data.attribute && this.data.attribute.data) {
        const labelAttributes = this.data.attribute.data
          .filter(item => item.id === this.typeLabelAttribute);
        if (labelAttributes && labelAttributes.length > 0) {
          mapObject.label = labelAttributes[0].value ? labelAttributes[0].value : mapObject.label;
          updatedLabel = true;
        }
      }
      if (!updatedLabel) {
        mapObject.label = this.mapObject.label;
      }
      this.updateMapObject({ mapObject });
    },
    updateStatus(value) {
      if (!value) {
        this.data.map_object_status_id = null;
      } else {
        this.data.map_object_status_id = value.id;
      }
    },
    validateFields() {
      let valid = true;
      if (this.data.name === '') {
        this.errors.name = this.$t('validation.name_empty');
        this.$notify({
          type: 'warning',
          title: this.$t('error.create_failed', { model: this.$tc('models.mapobject', 1) }),
          text: this.$t('validation.name_empty'),
          duration: 1000,
        });
        valid = false;
      }
      this.data.attribute.data.forEach((attribute) => {
        if (attribute.required
          && (attribute.value === undefined || attribute.value === null || attribute.value === '')
        ) {
          this.errors[attribute.id] = true;
          this.$notify({
            type: 'warning',
            title: this.$t('error.create_failed', { model: this.$tc('models.attribute', 1) }),
            text: this.$t('validation.field_empty', { field: attribute.name }),
            duration: 1000,
          });
          valid = false;
        }
        return attribute;
      });
      return valid;
    },
    resetError(field) {
      this.errors[field] = null;
    },
    clickBack() {
      this.$router.back();
    },
    clickParent() {
      EventBus.$emit('Map.changeMapMode', MapMode.objectSelect);
      this.$router.push({ name: 'drawing' });
    },
    clickChangeType() {
      this.changetype = !this.changetype;
    },
    getMapObjectStatuses() {
      MapObjectTypeApi.getMapObjectTypeStatusById(this.mapObject.map_object_type_id)
        .then((response) => {
          this.mapObjectStatuses = response.data;
          const currentStatus = this.mapObjectStatuses.filter(item => item.id === this.mapObject.map_object_status_id);
          if (currentStatus && currentStatus.length > 0) {
            const [first] = currentStatus;
            this.mapObjectStatus = first;
          }
        });
      this.loading = false;
    },
    getParent() {
      if (!this.mapObject || this.mapObject.parent_id === null) {
        return;
      }
      this.loading = true;
      MapObjectApi.getParentForMapObject(this.mapObject.id, 'mapObjectType')
        .then((response) => {
          this.parent = response.data;
          this.loading = false;
        });
    },
    ChangeParent() {
      this.showChangeParent = !this.showChangeParent;
    },
  },
  mounted() {
    if (this.$route.params.id !== null && this.$route.params.id !== undefined) {
      const id = parseInt(this.$route.params.id, 10);
      if (!this.mapObject || this.mapObject.id !== id) {
        this.fetchMapObject({ id }).then(() => {
          this.getMapObjectStatuses();
          this.getParent();
        });
      } else {
        this.getMapObjectStatuses();
        this.getParent();
        this.data = JSON.parse(JSON.stringify(this.mapObject));
        MapObjectTypeApi.getMapObjectTypeAttributes(this.mapObject.map_object_type_id, 'attributeLookupValue').then((response) => {
          this.data.attribute.data.map((data) => {
            const attribute = data;
            const search = response.data.filter(item => item.id === attribute.id);
            if (search.length === 0) {
              return attribute;
            }
            const typeAttribute = search[0];
            if (typeAttribute.default_value) {
              attribute.default_value = typeAttribute.default_value;
            }
            return attribute;
          });
          this.attributes = response.data;
        }).finally(() => {
          this.loadingAttributes = false;
        });
      }
      EventBus.$emit(
        'Map.changeMapMode',
        MapMode.objectSelect,
      );
    }
  },
  watch: {
    // eslint-disable-next-line func-names
    '$route.params.id': function (id) {
      if (id !== null && id !== undefined) {
        console.log('drawing/ShowObject.$route.params.id watcher');
        const startTime = performance.now();
        this.loading = true;
        this.selectedId = parseInt(id, 10);
        if (!this.mapObject || this.mapObject.id !== this.selectedId) {
          this.fetchMapObject({ id: this.selectedId }).then((response) => {
            if (!response) {
              setTimeout(() => {
                this.$router.push({ name: 'drawing' });
              }, 200);
            }
          }).finally(() => {
            this.loading = false;
          });
        }
        const endTime = performance.now();
        console.log(`drawing/ShowObject.$route.params.id watcher took ${endTime - startTime} milliseconds`);
      }
    },
    mapObject(value) {
      if (value) {
        this.data = JSON.parse(JSON.stringify(value));
        console.log('drawing/ShowObject.mapObject watcher');
        const startTime = performance.now();
        this.resetSelected();
        if (value.geoObject.data.geometry.type === 'LineString') {
          const startPointId = this.getStartPointIdForLine(value);
          if (startPointId) {
            const startPoint = dataCache.getMapObjectById(startPointId);
            if (startPoint) {
              this.addSelected({ mapObject: startPoint });
            } else {
              this.addSelected({ mapObject: { id: startPointId } });
            }
          }
          const endPointId = this.getEndPointIdForLine(value);
          if (endPointId) {
            const endPoint = dataCache.getMapObjectById(endPointId);
            if (endPoint) {
              this.addSelected({ mapObject: endPoint });
            } else {
              this.addSelected({ mapObject: { id: endPointId } });
            }
          }
        }
        this.addSelected({ mapObject: value });
        const endTime = performance.now();
        console.log(`drawing/ShowObject.mapObject watcher took ${endTime - startTime} milliseconds`);
      }
    },
  },
};
</script>

<style scoped>
.field {
  overflow: visible;
  min-height: 40px;
}
.object-information-block {
  padding: 0 20px;
}

.object-information-block h3.block-header {
  text-transform: uppercase;
  font-style: normal;
  font-weight: 400;
  font-size: 1em;
  margin: 0 0 5px;
  padding: 0;
  color: #42464c;
}

.object-information-block ul.block-details {
  overflow: auto;
  zoom: 1;
  font-size: .85em;
  margin: 0 0 20px 0;
  padding: 0;
}

.object-information-block ul.block-details li.block-item {
  line-height: 1.7em;
  position: relative;
  clear: both;
  list-style: none;
}

.object-information-block .block-details .block-item .block-item-title {
  display: block;
  float: left;
  width: 40%;
}

.object-information-block .block-details .parent-selections {
  margin-left: 30px;
  font-size: 1.1em;
}
.edit-object-buttons {
  padding: 10px 20px;
  position: relative;
  background: #f2f7fe;
  min-height: 50px;
  border-bottom: 1px solid #d9dadb;
}

.edit-object-buttons .save-button {
  float: right;
  margin-right: 7px;
}
.field.content {
  display: inline-flex;
  float: initial;
  overflow: initial;
  width: 100%;
}

.required {
  color: red;
}
</style>
